import { useState } from 'react'
import { Link } from 'react-router-dom'
import { formatDayTime, formatPhone, parseMs, withCountRu } from '@dorixona/utils'
import { Icon } from '../../ui/Icon'
import { Failed } from '../../ui/Load'
import { TableSkeleton } from '../../ui/Skeleton'
import { Pagination } from '../../ui/Pagination'
import { Select } from '../../ui/Select'
import { PageActions } from '../../app/PageHeader'
import { useReviews } from './api'
import { Stars } from './Stars'

const PAGE_SIZES = [10, 25, 50] as const

const RATINGS = [
  { value: 0, label: 'Все оценки' },
  ...[5, 4, 3, 2, 1].map((n) => ({ value: n, label: `${n} ${'★'.repeat(n)}` })),
]

/** Плохая оценка — то, что надо разобрать: жёлтым, остальное спокойно. */
const tone = (avg: number) => (avg < 3.5 ? 'warning' : undefined)

/**
 * Отзывы покупателей об аптеках — после закрытого заказа. Только для чтения
 * и только супер-админу. Сверху — средняя по сети и по аптекам (хуже
 * оценённые первыми, по ним — фильтр), ниже — сами отзывы, новые сверху.
 */
export function ReviewsScreen() {
  const [storeId, setStoreId] = useState(0)
  const [rating, setRating] = useState(0)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState<number>(PAGE_SIZES[0])
  // Средние по всем аптекам — без фильтров: иначе выбранная аптека
  // вытеснила бы остальные из полосы и из списка аптек.
  const overall = useReviews({ page: 1, pageSize: 1 })
  const list = useReviews({
    page,
    pageSize,
    storeId: storeId || null,
    rating: rating || null,
  })
  const stores = overall.data?.summary.by_store ?? []

  const pickStore = (id: number) => {
    setStoreId(id)
    setPage(1)
  }

  return (
    <>
      <PageActions>
        {overall.data ? (
          <span
            className="ui-count adm-count"
            aria-label={withCountRu(overall.data.summary.count, ['отзыв', 'отзыва', 'отзывов'])}
          >
            {overall.data.summary.count}
          </span>
        ) : null}
      </PageActions>

      {overall.data && overall.data.summary.count > 0 ? (
        <section className="ui-card rv-summary" aria-label="Средние оценки">
          <div className="rv-overall">
            <span className="rv-avg">{overall.data.summary.avg.toFixed(1)}</span>
            <Stars rating={overall.data.summary.avg} size={16} />
            <span className="rv-muted">по сети</span>
          </div>
          <ul className="rv-stores">
            {stores.map((store) => {
              const body = (
                <>
                  <span className="rv-store-name">{store.store_name}</span>
                  <span className="ui-status" data-tone={tone(store.avg)}>
                    {store.avg.toFixed(1)}
                  </span>
                  <span className="rv-muted">
                    {withCountRu(store.count, ['отзыв', 'отзыва', 'отзывов'])}
                  </span>
                </>
              )
              const id = store.store_id
              return (
                <li key={String(id)}>
                  {/* Заказ без аптеки отфильтровать нечем — просто плашка. */}
                  {id === null ? (
                    <span className="rv-store">{body}</span>
                  ) : (
                    <button
                      type="button"
                      className="rv-store"
                      aria-pressed={storeId === id}
                      onClick={() => {
                        pickStore(storeId === id ? 0 : id)
                      }}
                    >
                      {body}
                    </button>
                  )}
                </li>
              )
            })}
          </ul>
        </section>
      ) : null}

      <div className="ui-toolbar">
        <Select
          label="Аптека"
          value={storeId}
          active={storeId !== 0}
          options={[
            { value: 0, label: 'Все аптеки' },
            ...stores.flatMap((s) =>
              s.store_id === null
                ? []
                : [{ value: s.store_id, label: s.store_name, count: s.count }],
            ),
          ]}
          onChange={pickStore}
        />
        <Select
          label="Оценка"
          value={rating}
          active={rating !== 0}
          options={RATINGS}
          onChange={(value) => {
            setRating(value)
            setPage(1)
          }}
        />
      </div>

      <section className="ui-table-card" aria-label="Отзывы">
        {list.isPending ? <TableSkeleton what="отзывы" /> : null}
        {list.isError ? (
          <Failed
            what="отзывы"
            error={list.error}
            onRetry={() => {
              void overall.refetch()
              void list.refetch()
            }}
          />
        ) : null}
        {list.data ? (
          <>
            <div className="ui-table-wrap">
              <table className="ui-table ui-reviews">
                <colgroup>
                  <col className="w-date" />
                  <col className="w-rating" />
                  <col />
                  <col className="w-store" />
                  <col className="w-who" />
                </colgroup>
                <thead>
                  <tr>
                    <th scope="col">Когда</th>
                    <th scope="col">Оценка</th>
                    <th scope="col">Комментарий</th>
                    <th scope="col">Аптека и заказ</th>
                    <th scope="col">Покупатель</th>
                  </tr>
                </thead>
                <tbody>
                  {list.data.items.length === 0 ? (
                    <tr>
                      <td colSpan={5}>
                        <div className="ui-empty">
                          <Icon name="i-star" />
                          <b>{storeId || rating ? 'Таких отзывов нет' : 'Отзывов пока нет'}</b>
                        </div>
                      </td>
                    </tr>
                  ) : null}
                  {list.data.items.map((review) => (
                    <tr key={review.id}>
                      <td>{formatDayTime(parseMs(review.created_at))}</td>
                      <td>
                        <Stars rating={review.rating} />
                      </td>
                      <td className="rv-comment">
                        {review.comment || <span className="rv-muted">без комментария</span>}
                      </td>
                      <td>
                        <div className="rv-two">
                          <span>{review.store_name}</span>
                          <Link to={`/orders/${review.order_id}`}>
                            заказ №{review.order_number}
                          </Link>
                        </div>
                      </td>
                      <td>
                        <div className="rv-two">
                          <Link to={`/customers/${review.customer_id}`}>
                            {review.customer_name}
                          </Link>
                          <span className="rv-muted">{formatPhone(review.customer_phone)}</span>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pagination
              label="Страницы отзывов"
              page={page}
              pageSize={pageSize}
              total={list.data.total}
              sizes={PAGE_SIZES}
              busy={list.isPlaceholderData}
              onPage={setPage}
              onPageSize={(size) => {
                setPageSize(size)
                setPage(1)
              }}
            />
          </>
        ) : null}
      </section>
    </>
  )
}
